<script setup lang="ts">
// An input's form (EditDrawer): its source, format, slate, overlay,
// fallback picture and multiview.
import { computed, reactive, ref, watch } from 'vue'
import { api, type AES67Stream, type EditTarget, type Input } from '../../api'
import { casterBody, returnChoice, returnLeaders, withReturnChoice } from '../../caster'
import { hasFeature } from '../../features'
import { fpsLabel, freePort, newId, ristListenPort, slug, urlPort } from '../../util'
import MultiviewEditor from '../MultiviewEditor.vue'
import PickSelect from '../PickSelect.vue'
import RistOptions from './RistOptions.vue'
import { clone, ipKinds, newInputFormat, useDeckLink, useEditContext, useMedia, usePorts } from './context'

const props = defineProps<{ target: EditTarget }>()
const t = props.target
const { node, cfg, player, ipv4, gpus, isNew } = useEditContext(t)
function blankInput(): Input {
    return {
        id: '',
        name: '',
        kind: player.value ? 'srt_caller' : 'srt_listener',
        port: 9000,
        srt: { latency_ms: 200 },
        // a kind with a source follows it, the others start at 1080p59.94
        // (newInputFormat). A player's input is not decoded: no format.
        ...(player.value ? { width: 0, height: 0, fps: '' } : newInputFormat('srt_listener')),
        slate_enabled: false,
        slate_text: '',
    }
}
const input = reactive<Input>(
    t.kind === 'input' ? clone(cfg.value.inputs.find((i) => i.id === t.id) ?? blankInput()) : blankInput(),
)
input.fallback_media_id ??= '' // the selects' "freeze" and "none" options
input.overlay_media_id ??= ''
input.interface ??= ''
// SSM senders, typed as a list: addresses at the venue, not the node's
const sourcesText = ref((input.sources ?? []).join(', '))
// the node's interfaces by name, with their IPv4 addresses (PLY-6: a
// name keeps up with a new DHCP lease, an address does not)
const ifaces = computed(() =>
    (node.status?.machine.interfaces ?? []).map((i) => ({
        name: i.name,
        v4: i.addresses.filter((a) => /^\d+\.\d+\.\d+\.\d+\//.test(a)).map((a) => a.split('/')[0]),
    })),
)
input.decode ??= ''
input.deinterlace ??= ''
input.colour ??= ''
input.bit_depth ??= 0
// a new input changing kind takes the new kind's starting format, unless
// its format was changed from the old kind's: before the watchers below,
// which fill in what an audio input, a multiview and a caster need
watch(
    () => input.kind,
    (k, old) => {
        if (!isNew.value || player.value) return
        const was = newInputFormat(old)
        if (input.width === was.width && input.height === was.height && input.fps === was.fps)
            Object.assign(input, newInputFormat(k))
    },
)
// a multiview has its own size and rate (no source to follow) and starts as 2x2
const isMultiview = computed(() => input.kind === 'multiview')
// an audio input (sound card or AES67): its picture is black at a set size
// and rate, as a multiview's, and starts small
const isAudio = computed(() => input.kind === 'audio')
const soundCards = computed(() => node.status?.machine.sound_cards ?? [])
watch(
    () => input.kind,
    (k) => {
        if (k !== 'audio') return
        input.audio_in ??= { source: 'alsa', channels: 2 }
        if (input.audio_in.source === 'alsa') input.device ||= soundCards.value[0]?.device
        if (!input.width) [input.width, input.height] = [640, 360]
        input.fps ||= '25'
    },
    { immediate: true },
)
const soundCardNote = computed(() => node.status?.machine.sound_card_note ?? '')
// AES67 streams the node hears (SAP), for the stream picker
const aesFound = ref<AES67Stream[]>([])
const aesErr = ref('')
const aesLooking = ref(false)
async function findAES67() {
    aesErr.value = ''
    aesLooking.value = true
    try {
        const r = await api.aes67Streams()
        aesFound.value = r.streams
        if (r.errors.length) aesErr.value = 'not listened on: ' + r.errors.join('; ')
    } catch (e) {
        aesErr.value = e instanceof Error ? e.message : String(e)
    } finally {
        aesLooking.value = false
    }
}
// a sound card or a stream: another source starts empty; streams are looked for
function setAudioSource(source: 'alsa' | 'aes67') {
    input.audio_in = source === 'alsa' ? { source, channels: 2 } : { source }
    if (source === 'aes67') void findAES67()
}
// the picked stream: its description, and the interface it was heard on
function pickAES67(key: string) {
    const f = aesFound.value.find((s) => s.id + '|' + s.interface === key)
    if (!f) return
    input.audio_in = { source: 'aes67', sdp: f.sdp }
    input.interface = f.interface
}
const aesPicked = computed(() => {
    const sdp = input.audio_in?.sdp?.trim()
    return aesFound.value.find((s) => s.sdp.trim() === sdp && s.interface === input.interface)
})
// a player input (DECISIONS 2026-10-22 "player inputs"): several players
// connect, the studio takes one on air, nothing goes back to them
const casterPlayers = computed({
    get: () => !!input.caster?.players,
    set: (v: boolean) => (input.caster = { ...input.caster, players: v || undefined }),
})
// a caster input's route and bitrate cap (its picture: the format below)
const casterRoute = computed({
    get: () => input.caster?.route ?? '',
    set: (v: '' | 'relay' | 'direct') => (input.caster = { ...input.caster, route: v }),
})
// the return feed: an input, or another caster's return feed ("caster:<id>":
// the node copies its settings in and the two share one encode)
const casterReturn = computed({
    get: () => returnChoice(input.caster),
    set: (v: string) => (input.caster = withReturnChoice(input.caster, v)),
})
const leaders = computed(() => returnLeaders(cfg.value.inputs, input.id))
const leaderName = computed(() => cfg.value.inputs.find((i) => i.id === input.caster?.return_from)?.name)
// a new caster input takes the first caster's return feed: one encode for
// the casters of a show unless one is set otherwise
watch(
    () => input.kind,
    (k) => {
        if (k !== 'caster' || !isNew.value || input.caster?.return_from || input.caster?.return_input) return
        const lead = leaders.value[0]
        if (lead) input.caster = withReturnChoice(input.caster, 'caster:' + lead.id)
    },
    { immediate: true },
)
// production's mix for this caster: which pair of the return input's
// channels they hear (changed live, the caster stays connected)
// low latency: one resend at most, one frame waiting, the browser's frames
// as they come (CasterOpts.latency)
const casterLatency = computed({
    get: () => input.caster?.latency ?? '',
    set: (v: '' | 'low') => (input.caster = { ...input.caster, latency: v || undefined }),
})
const casterReturnPair = computed({
    get: () => input.caster?.return_pair ?? 0,
    set: (v: number) => (input.caster = { ...input.caster, return_pair: v || undefined }),
})
const casterReturnHeight = computed({
    get: () => input.caster?.return_height ?? 0,
    set: (v: number) => (input.caster = { ...input.caster, return_height: v || undefined }),
})
const casterReturnFPS = computed({
    get: () => input.caster?.return_fps ?? '',
    set: (v: '' | 'full') => (input.caster = { ...input.caster, return_fps: v || undefined }),
})
const casterReturnEncoder = computed({
    get: () => input.caster?.return_encoder ?? '',
    set: (v: '' | 'cpu' | 'gpu') => (input.caster = { ...input.caster, return_encoder: v || undefined }),
})
const casterReturnBitrate = computed({
    get: () => input.caster?.return_bitrate_k || undefined,
    set: (v: number | undefined) => (input.caster = { ...input.caster, return_bitrate_k: v || undefined }),
})
// bitrates offered for a caster's picture and for the return feed, with
// the format each suits (H.264 from a browser, 8 Mb/s by default; the return
// is x264 at zero latency, 2.5 Mb/s by default); a value set before that is
// not one of them stays offered
const casterRates = [
    { k: 2500, note: '720p' },
    { k: 4000, note: '1080p30' },
    { k: 6000, note: '1080p30+' },
    { k: 8000, note: '1080p60' },
    { k: 10000, note: '1080p60+' },
    { k: 12000, note: '1080p60++' },
    { k: 16000, note: 'fast motion' },
]
const returnRates = [
    { k: 1000, note: '360p' },
    { k: 1500, note: '540p' },
    { k: 2500, note: '720p' },
    { k: 4000, note: '720p+' },
    { k: 6000, note: '1080p' },
    { k: 8000, note: '1080p+' },
]
const withCurrent = (rates: { k: number; note: string }[], cur: number | undefined) =>
    cur && !rates.some((r) => r.k === cur) ? [...rates, { k: cur, note: 'set' }].sort((a, b) => a.k - b.k) : rates
const mbit = (k: number) => `${k / 1000} Mb/s`
const casterBitrate = computed({
    get: () => input.caster?.bitrate_k || undefined,
    set: (v: number | undefined) => (input.caster = { ...input.caster, bitrate_k: v || undefined }),
})
watch(
    () => input.kind,
    (k) => {
        if (k !== 'multiview') return
        input.multiview ??= { layout: '2x2', tiles: [], audio_tile: 0 }
        if (!input.width) [input.width, input.height] = [1920, 1080]
        input.fps ||= '60000/1001'
    },
    { immediate: true },
)
// a caster input runs at a set rate, not the camera's (it drifts; an SDI
// output of the input needs the rate it was given): one without a rate at
// 59.94, the default a new input has and the node's for one saved without
watch(
    () => input.kind,
    (k) => {
        if (k === 'caster' && !input.fps) input.fps = '60000/1001'
    },
    { immediate: true },
)
// NVDEC decoding is offered where it applies: an IP or media input on a node with an NVIDIA GPU
const decodable = computed(
    () => !player.value && gpus.value.length > 0 && (ipKinds.includes(input.kind) || input.kind === 'media'),
)
// deinterlacing: inputs capture decodes through ffmpeg
const deinterlaceable = computed(
    () => !player.value && (ipKinds.includes(input.kind) || input.kind === 'media' || input.kind === 'decklink'),
)
// colour and bit depth: those, and NDI
const colourable = computed(() => deinterlaceable.value || (!player.value && input.kind === 'ndi'))
function setSize(k: 'width' | 'height', e: Event) {
    input[k] = Number((e.target as HTMLInputElement).value) || 0
}
const { decklinkIn } = useDeckLink(() => ({ kind: 'input', id: input.id }))
// SMPTE 2110 (DECISIONS 2026-10-10 "SMPTE 2110"): a DeckLink device the
// machine lists as a DeckLink IP card's receives what its senders' SDPs
// describe
const ipDevice = computed(() => node.status?.machine.decklink_ip?.find((d) => d.device === input.device))
const st2110 = reactive({
    video_sdp: input.st2110?.video_sdp ?? '',
    audio_sdp: input.st2110?.audio_sdp ?? '',
    anc_sdp: input.st2110?.anc_sdp ?? '',
})
type SDPKey = keyof typeof st2110
const sdpFields: { key: SDPKey; label: string; type: string }[] = [
    { key: 'video_sdp', label: 'Video (2110-20): its SDP', type: 'video' },
    { key: 'audio_sdp', label: 'Audio (2110-30): its SDP, if it sends one', type: 'audio' },
    { key: 'anc_sdp', label: 'Ancillary data (2110-40, captions): its SDP, if it sends one', type: 'anc' },
]
const sdpURL = reactive<Record<SDPKey, string>>({ video_sdp: '', audio_sdp: '', anc_sdp: '' })
const sdpErr = ref('')
async function fetchSDP(key: SDPKey) {
    sdpErr.value = ''
    try {
        st2110[key] = await api.fetchSDP(sdpURL[key].trim())
    } catch (e) {
        sdpErr.value = e instanceof Error ? e.message : String(e)
    }
}
// the device's receiving flow of a type: one it has not got cannot be set
const receives = (type: string) => ipDevice.value?.flows.some((f) => f.direction === 'in' && f.type === type) ?? false
const media = useMedia()
// a backup input (failover.go): another picture input of the node with no
// backup of its own and the same format where both set one; an input that
// is itself another's backup takes none (backups do not chain)
const backupOf = computed(() =>
    cfg.value.inputs.filter((i) => !!input.id && i.id !== input.id && i.backup?.input_id === input.id),
)
const backupChoices = computed(() =>
    cfg.value.inputs
        .filter((i) => i.id !== input.id && i.kind !== 'multiview' && i.kind !== 'audio')
        .map((i) => {
            const fixed = Number(input.width) && i.width
            const why = i.backup
                ? 'has a backup of its own'
                : fixed &&
                    (Number(input.width) !== i.width ||
                        Number(input.height) !== i.height ||
                        input.fps !== i.fps ||
                        (Number(input.bit_depth) || 0) !== (i.bit_depth ?? 0))
                  ? `${i.width}x${i.height} ${fpsLabel(i.fps)}: not this format`
                  : ''
            return { id: i.id, name: i.name, why }
        }),
)
const backupId = computed({
    get: () => input.backup?.input_id ?? '',
    set: (v: string) => (input.backup = v ? { ...input.backup, input_id: v } : undefined),
})
const backupReturn = computed({
    get: () => input.backup?.return ?? '',
    set: (v: '' | 'manual') => input.backup && (input.backup.return = v || undefined),
})
// NDI sources the node sees, for the input's source field
const ndiFound = ref<{ name: string; url?: string }[]>([])
const ndiErr = ref('')
const ndiLooking = ref(false)
async function findNDI(refresh = false) {
    ndiErr.value = ''
    ndiLooking.value = true
    try {
        ndiFound.value = await api.ndiSources(refresh)
    } catch (e) {
        ndiErr.value = e instanceof Error ? e.message : String(e)
    } finally {
        ndiLooking.value = false
    }
}
// the picker lists what the node sees as soon as the kind is NDI
watch(
    () => input.kind,
    (k) => {
        if (k === 'ndi' && !ndiFound.value.length) void findNDI()
    },
    { immediate: true },
)
// ports: what this input would listen on, and who else listens on this node
const myPort = computed((): { proto: 'udp' | 'tcp'; port: number } | null => {
    if (input.kind === 'srt_listener' || input.kind === 'caster') return { proto: 'udp', port: Number(input.port) || 0 }
    if (input.kind === 'rtmp_listener') return { proto: 'tcp', port: urlPort(input.url, 1935) }
    if (input.kind === 'rist' && ristListenPort(input.url)) return { proto: 'udp', port: ristListenPort(input.url) }
    return null
})
const { otherPorts, portClash, portNote } = usePorts(t, myPort)
// a new listener gets a free port straight away (the default when that is free)
watch(
    () => input.kind,
    () => {
        if (isNew.value && input.kind === 'srt_listener') input.port = freePort(otherPorts.value, 'udp', 9000)
        if (isNew.value && input.kind === 'caster') input.port = freePort(otherPorts.value, 'udp', 8200)
        if (input.kind === 'rist') {
            input.rist ??= {}
            if (isNew.value && !input.url?.startsWith('rist://'))
                input.url = `rist://@:${freePort(otherPorts.value, 'udp', 5000)}`
        }
    },
    { immediate: true },
)
// only the tiles in use, up to the last one set
function multiviewBody() {
    const m = input.multiview!
    const tiles = m.tiles.map((t) => ({
        source: t.source || undefined,
        label: t.label?.trim() || undefined,
        no_label: t.no_label || undefined,
        vu: t.vu || undefined,
        format: t.format || undefined,
    }))
    while (tiles.length && !tiles.at(-1)!.source && !tiles.at(-1)!.label) tiles.pop()
    return {
        layout: m.layout,
        tiles,
        audio_tile: (m.audio_tile ?? 0) <= tiles.length ? m.audio_tile || undefined : undefined,
    }
}
async function save() {
    const id = input.id || newId(slug(input.name) + '-', new Set(cfg.value.inputs.map((i) => i.id)))
    const body = {
        ...input,
        id,
        width: Number(input.width) || 0,
        height: Number(input.height) || 0,
        port: (input.kind === 'srt_listener' || input.kind === 'caster') && input.port ? Number(input.port) : undefined,
        caster: input.kind === 'caster' ? casterBody(input.caster) : undefined,
        rist: input.kind === 'rist' ? input.rist : undefined,
        jitter_ms: input.jitter_ms ? Number(input.jitter_ms) : undefined,
        srt: input.srt ? { ...input.srt, bitrate_k: Number(input.srt.bitrate_k) || undefined } : undefined,
        interface:
            (input.kind === 'udp' || (input.kind === 'audio' && input.audio_in?.source === 'aes67')) && input.interface
                ? input.interface
                : undefined,
        sources:
            input.kind === 'udp' && sourcesText.value.trim()
                ? sourcesText.value.split(/[\s,]+/).filter((a) => a !== '')
                : undefined,
        audio_in: isAudio.value
            ? input.audio_in?.source === 'aes67'
                ? { source: 'aes67' as const, sdp: input.audio_in.sdp?.trim() }
                : { source: 'alsa' as const, channels: Number(input.audio_in?.channels) || undefined }
            : undefined,
        device: isAudio.value && input.audio_in?.source === 'aes67' ? undefined : input.device,
        ndi_name: (input.kind === 'ndi' && input.ndi_name?.trim()) || undefined,
        url: input.kind === 'ndi' ? input.url?.trim() || undefined : input.url,
        multiview: isMultiview.value ? multiviewBody() : undefined,
        slate_frames: (input.slate_info && input.slate_frames) || undefined,
        skip_decode: (!player.value && ipKinds.includes(input.kind) && input.skip_decode) || undefined,
        decode: decodable.value && input.decode === 'nvdec' ? 'nvdec' : undefined,
        deinterlace: (deinterlaceable.value && input.deinterlace) || undefined,
        source_timecode: (deinterlaceable.value && input.source_timecode) || undefined,
        frame_aligned: (deinterlaceable.value && input.frame_aligned) || undefined,
        captions: (deinterlaceable.value && input.captions) || undefined,
        scte104: (input.kind === 'decklink' && input.scte104) || undefined,
        st2110:
            input.kind === 'decklink' && ipDevice.value && st2110.video_sdp.trim()
                ? {
                      video_sdp: st2110.video_sdp.trim() + '\r\n',
                      audio_sdp: st2110.audio_sdp.trim() ? st2110.audio_sdp.trim() + '\r\n' : undefined,
                      anc_sdp: st2110.anc_sdp.trim() ? st2110.anc_sdp.trim() + '\r\n' : undefined,
                  }
                : undefined,
        backup:
            !player.value && !isMultiview.value && !isAudio.value && input.backup?.input_id
                ? {
                      input_id: input.backup.input_id,
                      after_s: Number(input.backup.after_s) || undefined,
                      return: input.backup.return || undefined,
                      return_after_s: Number(input.backup.return_after_s) || undefined,
                  }
                : undefined,
        colour: (colourable.value && input.colour) || undefined,
        bit_depth: (colourable.value && Number(input.bit_depth)) || undefined,
        decode_gpu:
            decodable.value && input.decode === 'nvdec' && input.decode_gpu ? Number(input.decode_gpu) : undefined,
    }
    await node.save('inputs', id, body)
    node.select({ kind: 'input', id })
}
defineExpose({ save, snapshot: () => JSON.stringify(input) })
</script>

<template>
    <div class="mb-2.5">
        <label>Name</label
        ><input v-model="input.name" required autofocus :placeholder="player ? 'Stage feed' : 'SDI 1 · Main'" />
    </div>
    <div class="grid grid-cols-2 gap-2.5">
        <div class="mb-2.5">
            <label>Kind</label>
            <select v-model="input.kind">
                <option value="srt_listener">SRT listener (we receive)</option>
                <option value="srt_caller">SRT caller (we connect)</option>
                <option value="rist">RIST (we listen or connect)</option>
                <option value="rtmp_listener">RTMP listener (we receive)</option>
                <option value="rtmp_pull">RTMP pull</option>
                <option value="udp">UDP / RTP, multicast or unicast (MPEG-TS)</option>
                <option value="url">URL (anything ffmpeg opens)</option>
                <option v-if="!player" value="decklink" :disabled="!decklinkIn.length">
                    DeckLink SDI{{ decklinkIn.length ? '' : ' (no card detected)' }}
                </option>
                <option value="media">Media file (picture or video, looped)</option>
                <option value="fake">Test pattern</option>
                <option value="ndi">NDI</option>
                <option v-if="!player" value="multiview">Multiview (tiles of inputs, encodes and destinations)</option>
                <option v-if="!player" value="caster">Caster (a browser sends camera and microphone)</option>
                <option v-if="!player" value="audio">Audio (a sound card, Dante or AES67)</option>
            </select>
        </div>
        <div v-if="input.kind === 'srt_listener' || input.kind === 'caster'" class="mb-2.5">
            <label>Port (UDP)</label
            ><input v-model="input.port" type="number" :min="input.kind === 'caster' ? 1024 : 1" max="65535" />
        </div>
        <div v-if="input.kind === 'udp'" class="mb-2.5">
            <label>Interface</label
            ><select v-model="input.interface">
                <option value="">automatic (routing table)</option>
                <option v-for="i in ifaces" :key="i.name" :value="i.name">
                    {{ i.name }}{{ i.v4.length ? ' (' + i.v4.join(', ') + ')' : '' }}
                </option>
                <optgroup label="by address (stays on it)">
                    <option v-for="a in ipv4" :key="a" :value="a">{{ a }}</option>
                </optgroup>
            </select>
        </div>
        <div v-if="input.kind === 'udp'" class="mb-2.5">
            <label>Senders (SSM)</label
            ><input v-model="sourcesText" placeholder="any; or 10.0.0.1, 10.0.0.2" data-testid="udp-sources" />
        </div>
        <div v-if="input.kind === 'decklink'" class="mb-2.5">
            <label>SDI input</label
            ><select v-model="input.device">
                <option v-for="d in decklinkIn" :key="d.name" :value="d.name">{{ d.name }}</option>
            </select>
        </div>
        <fieldset v-if="input.kind === 'decklink' && ipDevice" class="mb-2.5 border-0 p-0" data-test="st2110-input">
            <legend class="mb-1 text-xs text-fg-2">
                SMPTE 2110: the streams this device receives, each as its sender's SDP (pasted, or fetched from the
                sender's URL by the node)
            </legend>
            <template v-for="f in sdpFields" :key="f.key">
                <div v-if="receives(f.type)" class="mb-2">
                    <label>{{ f.label }}</label>
                    <textarea
                        v-model="st2110[f.key]"
                        rows="4"
                        class="font-mono text-xs"
                        :placeholder="f.key === 'video_sdp' ? 'v=0 …' : 'none'"
                        :data-test="`st2110-${f.key}`"
                    />
                    <div class="mt-1 flex gap-1.5">
                        <input v-model="sdpURL[f.key]" class="text-xs" placeholder="http://sender/video.sdp" />
                        <button
                            type="button"
                            class="btn-ghost text-xs"
                            :disabled="!sdpURL[f.key].trim()"
                            @click="fetchSDP(f.key)"
                        >
                            Fetch
                        </button>
                    </div>
                </div>
            </template>
            <p v-if="sdpErr" class="mt-0 text-xs text-bad">{{ sdpErr }}</p>
            <p v-if="!st2110.video_sdp.trim()" class="mt-0 text-xs text-warn">
                Without the video's SDP it is captured as an SDI device: nothing comes in over the network.
            </p>
        </fieldset>
        <div v-if="input.kind === 'media'" class="mb-2.5">
            <label>File</label
            ><select v-model="input.media_id">
                <option v-for="m in media" :key="m.id" :value="m.id">{{ m.name }} ({{ m.kind }})</option>
            </select>
        </div>
    </div>
    <div v-if="['srt_caller', 'rtmp_listener', 'rtmp_pull', 'url', 'udp', 'rist'].includes(input.kind)" class="mb-2.5">
        <label>URL</label
        ><input
            v-model="input.url"
            class="font-mono tabular-nums"
            :placeholder="
                input.kind === 'rtmp_listener'
                    ? 'rtmp://0.0.0.0:1935/live/key'
                    : input.kind === 'srt_caller'
                      ? 'srt://host:9000'
                      : input.kind === 'udp'
                        ? 'udp://@239.1.1.1:5000 or rtp://239.1.1.1:5004'
                        : input.kind === 'rist'
                          ? 'rist://@:5000 (we listen) or rist://sender:5000'
                          : 'rtmp://…'
            "
        />
        <p v-if="input.kind === 'rist'" class="mt-1 mb-0 text-xs text-fg-3">
            <code>rist://@:5000</code>: this node listens on UDP 5000 and the sender connects to it (open the port);
            <code>rist://host:5000</code>: this node connects to a sender that listens.
        </p>
    </div>
    <template v-if="input.kind === 'ndi'">
        <div class="mb-2.5">
            <label>NDI source</label>
            <div class="flex gap-2">
                <PickSelect
                    v-model="input.ndi_name"
                    class="min-w-0 flex-1"
                    :options="ndiFound.map((s) => ({ value: s.name, label: s.url ? `${s.name} · ${s.url}` : s.name }))"
                    :looking="ndiLooking"
                    missing="not seen now"
                    other="a source the node does not see: type its name…"
                    placeholder="CAMERA-PC (OBS)"
                    data-test="ndi-source"
                />
                <button type="button" class="btn-ghost text-xs whitespace-nowrap" @click="findNDI(true)">
                    look again
                </button>
            </div>
            <p v-if="ndiErr" class="mt-1 mb-0 text-xs text-bad">{{ ndiErr }}</p>
        </div>
        <div class="mb-2.5">
            <label>Sender's address (optional: when it cannot be discovered)</label
            ><input v-model="input.url" placeholder="10.0.0.5:5961" class="font-mono tabular-nums" />
        </div>
        <p class="text-xs text-fg-3">
            Pick a source the node sees; one it cannot discover is typed as NDI shows it, with its address. With an
            address the node connects straight to the sender. Where the node looks (discovery server, extra addresses,
            multicast) is in the node settings.
        </p>
        <p class="text-xs text-fg-3">
            NDI® is a registered trademark of Vizrt NDI AB ·
            <a href="https://ndi.video/" target="_blank" rel="noopener">ndi.video</a>
        </p>
    </template>
    <div v-if="input.kind === 'caster'" class="grid grid-cols-2 gap-2.5">
        <label
            v-if="casterPlayers || hasFeature(node.features, 'caster_players')"
            class="col-span-2 mb-2.5 flex items-center gap-1.5"
            data-test="caster-players"
        >
            <input v-model="casterPlayers" type="checkbox" class="w-auto" />
            For players: any number connect and you take one on air; they get nothing back (no return feed, comms or
            talkback) and you may switch their camera and microphone
        </label>
        <div class="mb-2.5">
            <label>Route</label
            ><select v-model="casterRoute">
                <option value="">automatic: to this node's port, through central if that fails</option>
                <option value="relay">always through central (no port to open here)</option>
                <option value="direct">only to this node's port</option>
            </select>
        </div>
        <div class="mb-2.5">
            <label>Bitrate, at most</label
            ><select
                :value="casterBitrate ?? 8000"
                @change="casterBitrate = Number(($event.target as HTMLSelectElement).value)"
            >
                <option v-for="r in withCurrent(casterRates, casterBitrate)" :key="r.k" :value="r.k">
                    {{ mbit(r.k) }} · {{ r.note }}
                </option>
            </select>
        </div>
        <div class="mb-2.5">
            <label>Latency</label
            ><select v-model="casterLatency">
                <option value="">normal: rides out loss on long lines</option>
                <option value="low">low: least delay, for nearby casters</option>
            </select>
            <p v-if="casterLatency === 'low'" class="mt-1 mb-0 text-xs text-fg-3">
                A lost packet holds the picture for one resend at most (normal: two), one frame waits for the clock
                (normal: two) and frames are taken as they come. On a lossy line the picture glitches more often.
            </p>
        </div>
        <div v-if="!casterPlayers" class="mb-2.5">
            <label>Return feed (what the caster sees and hears)</label
            ><select v-model="casterReturn" data-test="return-feed">
                <option value="">none</option>
                <optgroup v-if="leaders.length" label="the same as another caster's">
                    <option v-for="i in leaders" :key="i.id" :value="'caster:' + i.id">same as {{ i.name }}</option>
                </optgroup>
                <optgroup label="an input">
                    <option v-for="i in cfg.inputs.filter((x) => x.id !== input.id)" :key="i.id" :value="i.id">
                        {{ i.name }}
                    </option>
                </optgroup>
            </select>
            <p v-if="input.caster?.return_from" class="mt-1 text-xs text-fg-3">
                One encode with {{ leaderName ?? input.caster.return_from }}: its input, picture, frame rate and
                encoder, following its changes. The audio pair below is this caster's own.
            </p>
        </div>
        <div v-if="casterReturn && !casterPlayers" class="mb-2.5">
            <label>Return audio: the caster's mix</label
            ><select
                v-model.number="casterReturnPair"
                title="the pair of the return input's channels this caster hears; changing it keeps them connected"
            >
                <option v-for="k in 8" :key="k" :value="k - 1">channels {{ 2 * k - 1 }}+{{ 2 * k }}</option>
            </select>
        </div>
        <div v-if="casterReturn && !casterPlayers && !input.caster?.return_from" class="mb-2.5">
            <label>Return picture · bitrate</label>
            <div class="flex gap-1.5">
                <select v-model.number="casterReturnHeight" class="w-auto">
                    <option :value="0">720p</option>
                    <option :value="540">540p</option>
                    <option :value="360">360p</option>
                    <option :value="1080">1080p</option>
                </select>
                <select
                    :value="casterReturnBitrate ?? 2500"
                    @change="casterReturnBitrate = Number(($event.target as HTMLSelectElement).value)"
                >
                    <option v-for="r in withCurrent(returnRates, casterReturnBitrate)" :key="r.k" :value="r.k">
                        {{ mbit(r.k) }} · {{ r.note }}
                    </option>
                </select>
            </div>
        </div>
        <div v-if="casterReturn && !casterPlayers && !input.caster?.return_from" class="mb-2.5">
            <label>Return frame rate · encoder</label>
            <div class="flex gap-1.5">
                <select
                    v-model="casterReturnFPS"
                    class="w-auto"
                    title="half the input's rate above 30 fps costs half the CPU; a caster follows the show at 29.97 fine"
                >
                    <option value="">half above 30 fps</option>
                    <option value="full">full</option>
                </select>
                <select
                    v-model="casterReturnEncoder"
                    title="automatic: the GPU's NVENC when one has a session to spare, else x264 on the CPU"
                >
                    <option value="">automatic</option>
                    <option value="cpu">CPU (x264)</option>
                    <option value="gpu">GPU (NVENC)</option>
                </select>
            </div>
        </div>
    </div>
    <p v-if="input.kind === 'caster'" class="text-xs text-fg-3">
        A caster opens an invite link in their browser and sends camera and microphone here over WebRTC, from anywhere.
        Open this UDP port on the node's firewall (and forward it to the node behind NAT); a caster who cannot reach it
        goes through central's relay instead.
    </p>
    <p v-if="input.kind === 'udp'" class="text-xs text-fg-3">
        A multicast group (239.x.x.x) is joined on the interface chosen above; a unicast address receives what is sent
        to this node on that port. A source-specific group (232.x.x.x) needs its senders named. A player receives
        multicast on a wired interface: Wi-Fi sends it at its slowest rate.
    </p>
    <p v-if="player" class="text-xs text-fg-3">
        A player receives an input only while it is on screen: the stream goes to the screen as it arrives, not decoded
        or converted here.
    </p>
    <template v-if="isAudio">
        <div class="mb-2.5">
            <label>Sound from</label
            ><select
                :value="input.audio_in?.source"
                data-test="audio-source"
                @change="setAudioSource(($event.target as HTMLSelectElement).value as 'alsa' | 'aes67')"
            >
                <option value="alsa">
                    a sound card on this node (a Dante PCIe card or AVIO adapter, a USB interface)
                </option>
                <option value="aes67">an AES67 stream on the network (Dante in AES67 mode too)</option>
            </select>
        </div>
        <template v-if="input.audio_in?.source === 'alsa'">
            <p v-if="soundCardNote" class="mt-0 text-xs text-warn" role="alert">{{ soundCardNote }}</p>
            <div class="grid grid-cols-[1fr_auto] gap-2.5">
                <div class="mb-2.5">
                    <label>Sound card</label
                    ><PickSelect
                        v-model="input.device"
                        :options="soundCards.map((c) => ({ value: c.device, label: c.name }))"
                        data-test="sound-card"
                    />
                </div>
                <div class="mb-2.5">
                    <label>Channels</label
                    ><input v-model.number="input.audio_in!.channels" type="number" min="1" max="16" class="w-20" />
                </div>
            </div>
            <p class="mt-0 text-xs text-fg-3">
                {{
                    soundCards.length
                        ? `${soundCards.length} sound card${soundCards.length > 1 ? 's' : ''} with an input on this node.`
                        : 'No sound card with an input on this node: plug one in (it shows here within 10 s).'
                }}
                A Dante AVIO adapter takes the channels subscribed to it in Dante Controller.
            </p>
        </template>
        <template v-else>
            <div class="mb-2.5">
                <label>Stream</label>
                <div class="flex gap-2">
                    <select
                        class="min-w-0 flex-1"
                        :value="aesPicked ? aesPicked.id + '|' + aesPicked.interface : ''"
                        data-test="aes67-stream"
                        @change="pickAES67(($event.target as HTMLSelectElement).value)"
                    >
                        <option value="">
                            {{
                                aesFound.length
                                    ? 'pick a stream heard on the network'
                                    : aesLooking
                                      ? 'looking…'
                                      : 'none heard: look again, or paste its description'
                            }}
                        </option>
                        <option v-for="f in aesFound" :key="f.id + f.interface" :value="f.id + '|' + f.interface">
                            {{ f.name }} · {{ f.channels }} ch {{ f.encoding }} · {{ f.group }}:{{ f.port }} · on
                            {{ f.interface }}
                        </option>
                    </select>
                    <button type="button" class="btn-ghost text-xs" @click="findAES67">look again</button>
                </div>
                <p v-if="aesErr" class="mt-1 mb-0 text-xs text-warn">{{ aesErr }}</p>
            </div>
            <div class="grid grid-cols-[1fr_auto] gap-2.5">
                <div class="mb-2.5">
                    <label>Description (SDP)</label
                    ><textarea
                        v-model="input.audio_in!.sdp"
                        rows="4"
                        class="font-mono text-[11px]"
                        placeholder="filled in when you pick a stream; or paste the stream's SDP"
                    />
                </div>
                <div class="mb-2.5">
                    <label>Interface</label
                    ><select v-model="input.interface">
                        <option value="">automatic</option>
                        <option v-for="a in ipv4" :key="a" :value="a">{{ a }}</option>
                    </select>
                </div>
            </div>
            <p class="mt-0 text-xs text-fg-3">
                Streams are found from their SAP announcements (AES67 devices send them; a Dante flow set to AES67 in
                Dante Controller too). The node joins the stream on the interface of the audio network.
            </p>
        </template>
        <div class="grid grid-cols-2 gap-2.5">
            <div class="mb-2.5">
                <label>Picture (black, for encodes and outputs)</label
                ><select
                    :value="`${input.width}x${input.height}`"
                    @change="
                        ;[input.width, input.height] = ($event.target as HTMLSelectElement).value
                            .split('x')
                            .map(Number) as [number, number]
                    "
                >
                    <option value="640x360">640×360</option>
                    <option value="1280x720">1280×720</option>
                    <option value="1920x1080">1920×1080</option>
                </select>
            </div>
            <div class="mb-2.5">
                <label>Frame rate</label
                ><select v-model="input.fps">
                    <option v-for="f in ['60000/1001', '60', '50', '30000/1001', '30', '25']" :key="f" :value="f">
                        {{ fpsLabel(f) }}
                    </option>
                </select>
            </div>
        </div>
    </template>
    <template v-if="isMultiview">
        <div class="grid grid-cols-2 gap-2.5">
            <div class="mb-2.5">
                <label>Resolution</label
                ><select
                    :value="`${input.width}x${input.height}`"
                    @change="
                        ;[input.width, input.height] = ($event.target as HTMLSelectElement).value
                            .split('x')
                            .map(Number) as [number, number]
                    "
                >
                    <option value="1920x1080">1920×1080</option>
                    <option value="1280x720">1280×720</option>
                    <option value="3840x2160">3840×2160</option>
                </select>
            </div>
            <div class="mb-2.5">
                <label>Frame rate</label
                ><select v-model="input.fps">
                    <option v-for="f in ['60000/1001', '60', '50', '30000/1001', '30', '25']" :key="f" :value="f">
                        {{ fpsLabel(f) }}
                    </option>
                </select>
            </div>
        </div>
        <MultiviewEditor v-model="input.multiview!" :self-id="input.id" :cfg="cfg" />
    </template>
    <div v-if="!player && !isMultiview && !isAudio" class="grid grid-cols-3 gap-2.5">
        <div class="mb-2.5">
            <label>Width</label
            ><input
                :value="input.width || ''"
                type="number"
                step="2"
                placeholder="auto"
                @input="setSize('width', $event)"
            />
        </div>
        <div class="mb-2.5">
            <label>Height</label
            ><input :value="input.height || ''" type="number" placeholder="auto" @input="setSize('height', $event)" />
        </div>
        <div class="mb-2.5">
            <label>Frame rate</label>
            <select v-model="input.fps">
                <option v-if="input.kind !== 'caster'" value="">auto</option>
                <option
                    v-for="f in ['60000/1001', '60', '50', '30000/1001', '30', '25', '24000/1001', '24']"
                    :key="f"
                    :value="f"
                >
                    {{ fpsLabel(f) }}
                </option>
            </select>
        </div>
    </div>
    <p v-if="myPort" class="text-xs" :style="{ color: portClash ? 'var(--bad)' : 'var(--fg-3)' }">
        {{ portNote }}
    </p>
    <p v-if="!player && !isMultiview && !isAudio" class="text-xs text-fg-3">
        Auto follows the source: the node detects its format and, when it changes, restarts the input to match (encodes
        restart with it). Set a size or rate to conform every source to it instead: scaled and rate-converted here,
        once, for every encode.<template v-if="input.kind === 'caster'">
            A caster's frame rate is always set: their camera's varies, and the input keeps this one.</template
        >
    </p>
    <div v-if="deinterlaceable" class="mb-2.5">
        <label>Deinterlacing</label
        ><select v-model="input.deinterlace">
            <option value="">off</option>
            <option value="auto">when the source says it is interlaced</option>
            <option value="on">always (a source that is interlaced but not marked)</option>
        </select>
        <p v-if="input.deinterlace" class="mt-1 mb-0 text-xs text-fg-3">
            One picture per field: set the frame rate to the field rate (50 for 1080i50) for full motion. Adds a frame
            of latency. Saving restarts the input.
        </p>
    </div>
    <div v-if="colourable" class="mb-2.5">
        <label>Bit depth</label
        ><select v-model.number="input.bit_depth">
            <option :value="0">
                {{
                    input.kind === 'decklink' || input.kind === 'ndi'
                        ? 'auto (10 bits for an HDR colour)'
                        : "auto (the source's)"
                }}
            </option>
            <option :value="8">8 bits</option>
            <option :value="10">10 bits</option>
        </select>
        <p
            v-if="input.bit_depth === 10 || (input.kind === 'decklink' && !input.bit_depth)"
            class="mt-1 mb-0 text-xs text-fg-3"
        >
            10 bits hold a third more memory per frame than 8; encodes choose their own depth (pixel format).
        </p>
    </div>
    <div v-if="colourable" class="mb-2.5">
        <label>Colour</label
        ><select v-model="input.colour">
            <option value="">auto (the source's tags; untagged is SDR)</option>
            <option value="sdr">SDR (BT.709)</option>
            <option value="hlg">HDR HLG (BT.2020)</option>
            <option value="pq">HDR PQ (BT.2020, HDR10)</option>
        </select>
        <p class="mt-1 mb-0 text-xs text-fg-3">
            What the picture is, for a source that does not say{{
                input.kind === 'decklink' ? ' (SDI never does here)' : ''
            }}: nothing is converted. Encodes tag their streams with it.
        </p>
    </div>
    <div v-if="deinterlaceable" class="mb-2.5">
        <label class="flex items-center gap-1.5"
            ><input v-model="input.source_timecode" type="checkbox" class="w-auto" /> read the source's timecode ({{
                input.kind === 'decklink' ? 'RP 188 from SDI' : 'from the stream: H.264 or MPEG-2'
            }})</label
        >
        <p v-if="input.source_timecode" class="mt-1 mb-0 text-xs text-fg-3">
            Shown in the input's detail; the slate's per-frame clock then draws it instead of the time of day.
        </p>
    </div>
    <div v-if="deinterlaceable && (node.pair || input.frame_aligned)" class="mb-2.5">
        <label class="flex items-center gap-1.5"
            ><input v-model="input.frame_aligned" type="checkbox" class="w-auto" data-test="input-frame-aligned" />
            frame-aligned (1+1): number its frames from the source's timecode, its PTS, or PTP</label
        >
        <p v-if="input.frame_aligned" class="mt-1 mb-0 text-xs text-fg-3">
            Both nodes of the pair, fed the same signal, give each frame the same number: from its timecode; without
            one, an SRT, UDP or RIST feed from its own PTS, anything else from the plant's PTP clock (set the node's PTP
            interface in its settings). The input's detail says which, or that its frames have none. Encodes do not take
            their timing from it yet (the next step of 1+1).
        </p>
    </div>
    <div v-if="deinterlaceable" class="mb-2.5">
        <label class="flex items-center gap-1.5"
            ><input v-model="input.captions" type="checkbox" class="w-auto" data-test="input-captions" /> carry the
            source's CEA-608/708 captions into its encodes ({{
                input.kind === 'decklink' ? 'from the SDI VANC' : "from the stream's H.264 or HEVC"
            }})</label
        >
        <p v-if="input.captions" class="mt-1 mb-0 text-xs text-fg-3">
            H.264 and HEVC encodes carry them on to every destination; each encode's detail says whether it does. A
            frame rate change keeps them only between 23.976, 24, 29.97, 30, 59.94 and 60: keep a 25 or 50 fps source at
            its own rate.<template v-if="input.kind === 'decklink'">
                The card hands its captions over only with 10-bit frames, so an 8-bit input takes those and converts
                them.</template
            >
            A passthrough carries captions without this.
        </p>
    </div>
    <div v-if="input.kind === 'decklink'" class="mb-2.5">
        <label class="flex items-center gap-1.5"
            ><input v-model="input.scte104" type="checkbox" class="w-auto" data-test="input-scte104" /> carry the SDI
            feed's SCTE-104 ad markers into its encodes (from the VANC)</label
        >
        <p v-if="input.scte104" class="mt-1 mb-0 text-xs text-fg-3">
            Each cue goes into the encodes with ad markers as SCTE-35, at its frame and pre-roll, as an IP feed's cues
            do. The card hands its VANC over only with 10-bit frames, so an 8-bit input takes those and converts them.
            Needs the node's ffmpeg -ese7 or later.
        </p>
    </div>
    <div v-if="!player && ipKinds.includes(input.kind)" class="mb-2.5">
        <label class="flex items-center gap-1.5"
            ><input v-model="input.skip_decode" type="checkbox" class="w-auto" /> Skip decoding while nothing needs the
            picture</label
        >
        <div class="text-xs text-fg-3">
            For distribution: an input only passthroughs read then costs no CPU. Its preview is a key frame every few
            seconds, without sound meters or black and freeze checks. An encode, an SDI output or watching it starts its
            decoding. Passthroughs never restart the input either way.
        </div>
    </div>
    <div v-if="decodable" class="grid grid-cols-2 gap-2.5">
        <div class="mb-2.5">
            <label>Decoding</label
            ><select v-model="input.decode">
                <option value="">CPU</option>
                <option value="nvdec">NVIDIA GPU (NVDEC)</option>
            </select>
        </div>
        <div v-if="input.decode === 'nvdec' && gpus.length > 1" class="mb-2.5">
            <label>GPU</label
            ><select v-model.number="input.decode_gpu">
                <option v-for="g in gpus" :key="g.index" :value="g.index">{{ g.index }} · {{ g.name }}</option>
            </select>
        </div>
    </div>
    <p v-if="decodable && input.decode === 'nvdec'" class="text-xs text-fg-3">
        Decodes H.264, HEVC and AV1 on the GPU, which saves about a quarter of a core per 1080p60 input; the conversion
        for the encoders stays on the CPU. A codec the GPU cannot decode falls back to the CPU. Saving restarts the
        input.
    </p>
    <details v-if="!player && !isMultiview && !isAudio">
        <summary>Jitter buffer</summary>
        <div class="mb-2.5">
            <label>Jitter buffer (ms)</label
            ><input v-model="input.jitter_ms" type="number" min="0" max="1000" step="10" placeholder="100" />
        </div>
        <p class="text-xs text-fg-3">
            Frames wait here for the node's clock, so a source that delivers in bursts plays smoothly. Raise it when the
            input shows frames repeated with no signal problem (typical for network sources); it adds up to this much
            latency. Saving restarts the input.
        </p>
    </details>
    <RistOptions v-if="input.kind === 'rist'" v-model="input.rist!" />
    <details v-if="input.kind.startsWith('srt')">
        <summary>SRT options</summary>
        <div class="grid grid-cols-4 gap-2.5">
            <div class="mb-2.5"><label>Latency (ms)</label><input v-model="input.srt!.latency_ms" type="number" /></div>
            <div class="mb-2.5">
                <label>Bitrate (kbit/s)</label
                ><input v-model="input.srt!.bitrate_k" type="number" min="0" step="1000" placeholder="25000" />
            </div>
            <div class="mb-2.5">
                <label>Passphrase</label
                ><input
                    v-model="input.srt!.passphrase"
                    type="password"
                    autocomplete="new-password"
                    title="Kept on the node and never shown again: type a new one to change it, clear it to remove it"
                />
            </div>
            <div class="mb-2.5"><label>Stream ID</label><input v-model="input.srt!.streamid" /></div>
        </div>
        <p class="text-xs text-fg-3">
            The bitrate sizes SRT's buffers for the latency: the most the link will carry, 25 Mbit/s if empty.
        </p>
    </details>
    <div v-if="!player && !isMultiview && !isAudio" class="mb-2.5">
        <label>On signal loss</label>
        <select v-model="input.fallback_media_id">
            <option value="">freeze the last frame</option>
            <option v-for="m in media.filter((x) => x.kind === 'image')" :key="m.id" :value="m.id">
                show picture: {{ m.name }}
            </option>
        </select>
        <p class="mt-1 mb-0 text-xs text-fg-3">
            The picture replaces the frozen frame two seconds after frames stop and disappears the moment they return.
            Encodes and destinations keep running either way.
        </p>
    </div>
    <details v-if="!player && !isMultiview && !isAudio" :open="!!input.backup?.input_id">
        <summary>Backup input</summary>
        <p v-if="backupOf.length" class="text-xs text-fg-3">
            This input is the backup of {{ backupOf.map((i) => i.name).join(', ') }}: backups do not chain.
        </p>
        <template v-else>
            <div class="mb-2.5">
                <label>When this input has no signal, its encodes and SDI/NDI outputs read</label>
                <select v-model="backupId" data-test="input-backup">
                    <option value="">nothing else (no backup)</option>
                    <option v-for="b in backupChoices" :key="b.id" :value="b.id" :disabled="!!b.why">
                        {{ b.name }}{{ b.why ? ' · ' + b.why : '' }}
                    </option>
                </select>
            </div>
            <div v-if="input.backup?.input_id" class="grid grid-cols-3 gap-2.5">
                <div class="mb-2.5">
                    <label>After (s without a signal)</label
                    ><input v-model.number="input.backup.after_s" type="number" min="0" max="600" placeholder="2" />
                </div>
                <div class="mb-2.5">
                    <label>Back</label>
                    <select v-model="backupReturn">
                        <option value="">automatically</option>
                        <option value="manual">by hand only</option>
                    </select>
                </div>
                <div v-if="!input.backup.return" class="mb-2.5">
                    <label>Once back for (s)</label
                    ><input
                        v-model.number="input.backup.return_after_s"
                        type="number"
                        min="0"
                        max="3600"
                        placeholder="10"
                    />
                </div>
            </div>
            <p class="text-xs text-fg-3">
                A live cut: destinations stay connected and nothing restarts. The switch waits for the backup to have a
                picture; the input's detail switches by hand, and a switch by hand stays until switched back. Only no
                signal switches: a frozen or black picture with a signal does not.
            </p>
        </template>
    </details>
    <details v-if="!player">
        <summary>Overlay</summary>
        <div class="mb-2.5">
            <label>Picture (PNG with transparency, from the media library)</label>
            <select v-model="input.overlay_media_id">
                <option value="">none</option>
                <option v-for="m in media.filter((x) => x.kind === 'image')" :key="m.id" :value="m.id">
                    {{ m.name }}
                </option>
            </select>
        </div>
        <p class="text-xs text-fg-3">
            Turn the overlay on and off from the detail panel. It is scaled to the input and drawn on every encode of
            it.
        </p>
    </details>
    <details v-if="!player">
        <summary>Slate</summary>
        <div class="mb-2.5">
            <label>Slate text</label><input v-model="input.slate_text" placeholder="DEBUG SLATE CPH01" />
        </div>
        <div class="mb-2.5">
            <label class="flex items-center gap-1.5"
                ><input v-model="input.slate_info" type="checkbox" class="w-auto" /> also show node, input, source
                format, signal state and clock</label
            >
        </div>
        <div v-if="input.slate_info" class="mb-2.5">
            <label class="flex items-center gap-1.5"
                ><input v-model="input.slate_frames" type="checkbox" class="w-auto" /> clock with the frame number,
                drawn on every frame (15:04:05.37), flashing on each new second</label
            >
        </div>
        <p class="text-xs text-fg-3">
            Turn the slate on and off from the detail panel; it is drawn over every encode of this input.
        </p>
    </details>
</template>
